import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

import { isNative } from "~/lib/capacitor";

/**
 * What the calendar file needs from an event — the member detail and the public
 * registration page both have it, so neither has to pretend to be the other.
 */
export interface CalendarEvent {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  starts_at: string;
  ends_at: string | null;
  location: string | null;
  /** The link the calendar entry carries, when there is one worth carrying. */
  url?: string | null;
}

/**
 * Put an event into the member's own calendar.
 *
 * The API is member-only, so a calendar app cannot be handed a URL to subscribe to;
 * the event is serialised here, as an iCalendar file, and handed over in whichever
 * way the platform can take a file:
 *
 * - **Web** — a download. Every desktop calendar and both mobile web calendars open
 *   an `.ics` from the downloads tray.
 * - **Native** — the share sheet, from a file written to the app's cache. The WebView
 *   cannot download, and the share sheet is where iOS and Android both offer "Add to
 *   Calendar" alongside the other targets.
 *
 * Times go out as UTC instants (`Z`), so the calendar app places the event in the
 * member's own zone the same way the app renders it. An open-ended event gets a
 * one-hour slot rather than no `DTEND`: a zero-length event renders as a bare line in
 * most calendars and is easy to miss.
 */

/** Default span, in minutes, of an event with no end. */
const DEFAULT_DURATION_MINUTES = 60;

/** The maximum octets on one content line before RFC 5545 folding kicks in. */
const FOLD_AT = 75;

const PRODUCT_ID = "-//Elyxeer//Member app//EN";

const FILE_TYPE = "text/calendar;charset=utf-8";

/** RFC 5545 date-time in UTC: `20260919T150000Z`. */
function icsTimestamp(iso: string): string {
  return new Date(iso)
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
}

/** Escape a TEXT value: backslash first, then the other reserved characters. */
function escapeText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

/**
 * Fold a content line every 75 octets, continuing on a line that starts with a space.
 *
 * Measured in bytes, not characters: a Czech title is mostly two-byte glyphs, and a
 * split inside one corrupts the file for strict parsers.
 */
function fold(line: string): string {
  const encoder = new TextEncoder();
  const parts: string[] = [];
  let current = "";
  let width = 0;
  for (const character of line) {
    const size = encoder.encode(character).length;
    // The continuation's leading space counts against the next line's budget.
    const limit = parts.length === 0 ? FOLD_AT : FOLD_AT - 1;
    if (width + size > limit) {
      parts.push(current);
      current = "";
      width = 0;
    }
    current += character;
    width += size;
  }
  parts.push(current);
  return parts.join("\r\n ");
}

/**
 * The iCalendar document for one event.
 *
 * Pure, so it is unit-tested byte for byte. `stamp` is the file's own creation time,
 * injectable for the same reason.
 */
export function buildIcs(
  event: CalendarEvent,
  stamp: Date = new Date(),
): string {
  const starts = new Date(event.starts_at);
  const ends = event.ends_at
    ? new Date(event.ends_at)
    : new Date(starts.getTime() + DEFAULT_DURATION_MINUTES * 60_000);

  const properties: [string, string][] = [
    ["UID", `${event.id}@elyxeer.com`],
    ["DTSTAMP", icsTimestamp(stamp.toISOString())],
    ["DTSTART", icsTimestamp(starts.toISOString())],
    ["DTEND", icsTimestamp(ends.toISOString())],
    ["SUMMARY", escapeText(event.title)],
  ];
  if (event.summary)
    properties.push(["DESCRIPTION", escapeText(event.summary)]);
  if (event.location) properties.push(["LOCATION", escapeText(event.location)]);
  if (event.url) properties.push(["URL", event.url]);

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    `PRODID:${PRODUCT_ID}`,
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    ...properties.map(([name, value]) => fold(`${name}:${value}`)),
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  // CRLF is the line terminator the format specifies, and a trailing one closes the
  // last line.
  return `${lines.join("\r\n")}\r\n`;
}

/** The file name a calendar app shows before the event is opened. */
export function icsFileName(event: CalendarEvent): string {
  return `${event.slug}.ics`;
}

async function downloadIcs(event: CalendarEvent, ics: string): Promise<void> {
  const url = URL.createObjectURL(new Blob([ics], { type: FILE_TYPE }));
  const link = document.createElement("a");
  link.href = url;
  link.download = icsFileName(event);
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

async function shareIcs(event: CalendarEvent, ics: string): Promise<void> {
  // The cache directory: the file is a hand-off, not something to keep, and the
  // system may reclaim it once the share sheet is done with it.
  const written = await Filesystem.writeFile({
    path: icsFileName(event),
    data: ics,
    directory: Directory.Cache,
    // Without an explicit encoding the data would be treated as base64.
    encoding: Encoding.UTF8,
  });
  await Share.share({ title: event.title, files: [written.uri] });
}

/**
 * Hand the event to the member's calendar.
 *
 * A dismissed share sheet rejects on both platforms; that is the member changing
 * their mind, not a failure, so it is swallowed here rather than surfaced.
 */
export async function addToCalendar(event: CalendarEvent): Promise<void> {
  const ics = buildIcs(event);
  if (!isNative()) {
    await downloadIcs(event, ics);
    return;
  }
  try {
    await shareIcs(event, ics);
  } catch {
    // Cancelled.
  }
}
